import { Button, Flex, JaneLogo, Link, Typography } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';

import { PasswordField } from '../passwordField/passwordField';

import styles from './welcomeBack.module.css';

export interface WelcomeBackFormData {
  password: string;
}

export interface WelcomeBackProps {
  /** Email of the returning user (display-only on this screen). */
  email: string;

  /** Errors from the sign-in attempt. */
  errors?: string[] | null;

  /**
   * Optional non-error message rendered above the form (e.g. a confirmation
   * that a password-reset email was sent). Styled distinctly from `errors`.
   */
  notice?: string | null;

  /** Function called when the Cancel button is clicked. */
  onCancel(): void;

  /** Function called when the Edit link is clicked (returns to email entry). */
  onEditEmail(): void;

  /** Function called when the Forgot password link is clicked. */
  onForgotPassword(): void;

  /** Function called when the form is submitted with the password. */
  onSubmit(data: WelcomeBackFormData): void;

  /** Optional React node to display at the bottom of the form. */
  signInFooter?: ReactNode;
}

export const WelcomeBack = ({
  email,
  errors,
  notice,
  onCancel,
  onEditEmail,
  onForgotPassword,
  onSubmit,
  signInFooter,
}: WelcomeBackProps) => {
  return (
    <Form
      name="welcome-back"
      onSubmit={(data) => onSubmit(data as unknown as WelcomeBackFormData)}
    >
      <Flex width="100%" flexDirection="column" alignItems="center">
        <Typography branded variant="h2-semibold" textAlign="center" mb={24}>
          Welcome back!
        </Typography>
        <Flex width="100%" className={styles['emailRow']}>
          <Typography as="span" variant="body-semibold">
            {email}
          </Typography>
          <Link onClick={onEditEmail}>Edit</Link>
        </Flex>
        {notice && (
          <Flex flexDirection="column" mb={16}>
            <Typography
              color="info"
              role="status"
              textAlign="center"
              variant="body-semibold"
            >
              {notice}
            </Typography>
          </Flex>
        )}
        <PasswordField autoFocus />
        {errors && errors.length > 0 && (
          <Flex flexDirection="column" mt={-12} mb={12}>
            {errors.map((error) => (
              <Typography key={error} color="error" textAlign="center">
                {error}
              </Typography>
            ))}
          </Flex>
        )}
        <Form.SubmitButton
          data-testid="welcome-back-submit-btn"
          full
          label="Sign in"
          mb={16}
          variant="primary"
        />
        <Button
          full
          label="Cancel"
          mb={16}
          onClick={onCancel}
          variant="tertiary"
        />
        <Link mb={24} onClick={onForgotPassword}>
          Forgot password?
        </Link>
        {signInFooter || (
          <Typography as="span" color="grays-mid" textAlign="center">
            Sign in to check your reservations, status updates, and personal
            details&mdash;securely backed by{' '}
            <JaneLogo
              className={styles['inlineJaneLogo']}
              size="xs"
              variant="secondary"
            />
          </Typography>
        )}
      </Flex>
    </Form>
  );
};
